import type { FormElement } from './types';

/**
 * How questions fall into rows.
 *
 * The runtime lays a page out as a twelve-column grid and lets items flow: a full-width
 * question spans twelve, a half spans six, a third spans four. A "row" is therefore not
 * something stored anywhere — it is whatever consecutive run of questions happens to fit
 * inside twelve columns. This module is the one place that arithmetic lives, so the
 * builder groups them exactly the way the runtime will.
 */

export const COLUMNS = 12;

export type Width = 'full' | 'half' | 'third';

export const SPAN: Record<Width, number> = { full: 12, half: 6, third: 4 };

/** The width a question is rendered at. Absent means full, which is the default. */
export function widthOf(element: FormElement): Width {
  const width = element.layout?.width;
  return width === 'half' || width === 'third' ? width : 'full';
}

/** The most questions that fit on one row. Three thirds; a fourth has nowhere to go. */
export const MAX_PER_ROW = 3;

/**
 * Groups consecutive elements into the rows the runtime will draw.
 *
 * A group element (columns block, repeating group) always takes a row of its own — it
 * spans the full width in the runtime's own stylesheet — so it ends whatever row was open.
 */
export function rowsOf(elements: FormElement[]): FormElement[][] {
  const rows: FormElement[][] = [];
  let current: FormElement[] = [];
  let used = 0;

  for (const element of elements) {
    const ownRow = element.kind === 'group';
    const span = ownRow ? COLUMNS : SPAN[widthOf(element)];

    if (ownRow || used + span > COLUMNS) {
      if (current.length) rows.push(current);
      current = [];
      used = 0;
    }

    current.push(element);
    used += span;

    if (ownRow || used >= COLUMNS) {
      rows.push(current);
      current = [];
      used = 0;
    }
  }

  if (current.length) rows.push(current);
  return rows;
}

/**
 * The width that shares a row evenly between `count` questions.
 *
 * Only three values exist, so anything above three has no honest answer — callers refuse
 * the drop rather than inventing a quarter the runtime cannot draw.
 */
export function evenWidth(count: number): Width {
  if (count <= 1) return 'full';
  if (count === 2) return 'half';
  return 'third';
}

/**
 * Writes a width onto an element, clearing it when it is the default.
 *
 * Mirrors what the properties panel does: "full" is the absence of a layout hint, not a
 * stored value, so a form nobody has laid out carries no layout at all.
 */
export function setWidth(element: FormElement, width: Width): void {
  if (width === 'full') {
    if (element.layout) element.layout.width = undefined;
    return;
  }

  element.layout = { ...element.layout, width };
}

/**
 * Closes up the rows an edit disturbed.
 *
 * Widths are only a hint about how much of a row a question wants, so any edit that moves,
 * removes or inserts one can leave a row that no longer fills its twelve columns — two
 * questions at a third each, or one stranded alone at a third. Nobody asked for that; it is
 * the debris of the edit. Given the refs the edit touched, this shares each affected row out
 * evenly again.
 *
 * Only rows containing a touched ref are rewritten, so a width somebody set by hand
 * elsewhere on the page survives. Setting widths changes which rows exist, so it runs until
 * the layout stops moving — bounded, because a pass that changes nothing is the end of it.
 */
export function rebalanceRows(elements: FormElement[], touched: Set<string>): void {
  for (let pass = 0; pass < 4; pass++) {
    let changed = false;

    for (const row of rowsOf(elements)) {
      if (!row.some((e) => touched.has(e.ref))) continue;

      // A group already owns its row and has no width of its own to share out.
      if (row.some((e) => e.kind === 'group')) continue;

      const width = evenWidth(row.length);
      for (const member of row) {
        if (widthOf(member) === width) continue;
        setWidth(member, width);
        changed = true;
      }
    }

    if (!changed) return;
  }
}

/** The refs sharing a row with `ref`, including it. Empty when the ref is not on the page. */
export function rowMates(elements: FormElement[], ref: string): string[] {
  const row = rowsOf(elements).find((r) => r.some((e) => e.ref === ref));
  return row ? row.map((e) => e.ref) : [];
}
